import { api, ErroreRete } from '../../api/client';
import { DEPOSITO_CODA, idb } from '../../lib/idb';
import type { RapportinoLocale, RisultatoSincronizzazione } from './tipi';

const LOTTO_MASSIMO = 50;

export const coda = {
  async accoda(rapportino: RapportinoLocale): Promise<void> {
    await idb.salva(DEPOSITO_CODA, rapportino);
  },

  async inAttesa(): Promise<RapportinoLocale[]> {
    const tutti = await idb.leggiTutti<RapportinoLocale>(DEPOSITO_CODA);
    return tutti.sort((a, b) => a.compilatoIlDispositivo.localeCompare(b.compilatoIlDispositivo));
  },

  async quantiInAttesa(): Promise<number> {
    return idb.conta(DEPOSITO_CODA);
  },

  /**
   * Svuota la coda. Le righe accettate dal server si cancellano; quelle rifiutate
   * restano con il motivo, perché l'operaio le possa correggere.
   * Se manca la rete non si tocca nulla e si riprova più tardi.
   */
  async sincronizza(): Promise<{ inviati: number; rifiutati: number; rimasti: number }> {
    const attesa = (await coda.inAttesa()).filter((r) => r.stato !== 'rifiutato');
    if (attesa.length === 0) {
      return { inviati: 0, rifiutati: 0, rimasti: await coda.quantiInAttesa() };
    }

    const lotto = attesa.slice(0, LOTTO_MASSIMO);

    let esiti: RisultatoSincronizzazione[];
    try {
      esiti = await api.post<RisultatoSincronizzazione[]>('/rapportini/sincronizza', {
        rapportini: lotto.map((r) => ({
          idDispositivo: r.idDispositivo,
          operaioId: r.operaioId,
          dataLavoro: r.dataLavoro,
          compilatoIlDispositivo: r.compilatoIlDispositivo,
          note: r.note,
          ore: r.ore.map((o) => ({
            commessaId: o.commessaId,
            ore: o.ore,
            tipoLavorazione: o.tipoLavorazione,
            descrizione: o.descrizione,
          })),
          materiali: r.materiali.map((m) => ({
            commessaId: m.commessaId,
            descrizione: m.descrizione,
            quantita: m.quantita,
            unitaMisura: m.unitaMisura,
            siglaProduttore: m.siglaProduttore,
            codiceArticoloProduttore: m.codiceArticoloProduttore,
          })),
          // Le code vecchie non hanno il campo: si manda un elenco vuoto, non `undefined`.
          trasferte: (r.trasferte ?? []).map((t) => ({
            commessaId: t.commessaId,
            km: t.km,
            note: t.note,
          })),
          invia: true,
        })),
      });
    } catch (errore) {
      if (errore instanceof ErroreRete) {
        // Niente rete: la coda resta intatta, nessun tentativo consumato inutilmente.
        return { inviati: 0, rifiutati: 0, rimasti: await coda.quantiInAttesa() };
      }
      throw errore;
    }

    let inviati = 0;
    let rifiutati = 0;

    for (const esito of esiti) {
      const locale = lotto.find((r) => r.idDispositivo === esito.idDispositivo);
      if (!locale) continue;

      if (esito.esito === 'Creato' || esito.esito === 'GiaPresente' || esito.esito === 'Aggiornato') {
        await idb.elimina(DEPOSITO_CODA, esito.idDispositivo);
        inviati += 1;
      } else {
        await idb.salva(DEPOSITO_CODA, {
          ...locale,
          stato: 'rifiutato',
          tentativi: locale.tentativi + 1,
          ultimoErrore: esito.messaggio ?? 'Rifiutato dal server.',
        } satisfies RapportinoLocale);
        rifiutati += 1;
      }
    }

    return { inviati, rifiutati, rimasti: await coda.quantiInAttesa() };
  },

  async scarta(idDispositivo: string): Promise<void> {
    await idb.elimina(DEPOSITO_CODA, idDispositivo);
  },
};

/** Al ritorno della connessione la coda si svuota da sola: l'operaio non deve ricordarsene. */
export function avviaSincronizzazioneAutomatica(
  onAggiornamento: (rimasti: number) => void,
): () => void {
  let attivo = true;

  const tenta = async () => {
    if (!attivo || !navigator.onLine) return;
    try {
      const esito = await coda.sincronizza();
      onAggiornamento(esito.rimasti);
    } catch {
      // Un errore del server non deve fermare il ciclo: si ritenta al prossimo giro.
    }
  };

  window.addEventListener('online', tenta);
  const intervallo = window.setInterval(tenta, 60_000);
  void tenta();

  return () => {
    attivo = false;
    window.removeEventListener('online', tenta);
    window.clearInterval(intervallo);
  };
}
